import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import SmileArc from "@/components/SmileArc";
import RotatingBadge from "@/components/RotatingBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINES = ["EVERYDAY", "THINGS,", "MADE CLEVER."];
const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textYRaw = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = isTouch ? "0%" : textYRaw;
  // On touch devices: no staggered delays, quick reveals — everything appears instantly
  const d = (v) => (isTouch ? 0 : v);
  const t = (v) => (isTouch ? Math.min(v, 0.5) : v);

  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const minPrice = products.length
    ? Math.min(...products.map((p) => p.price))
    : null;

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-24"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="aurora-a absolute -left-40 top-10 h-[34rem] w-[34rem] rounded-full bg-ember/25 blur-3xl" />
        <div className="aurora-b absolute -right-32 bottom-0 h-[28rem] w-[28rem] rounded-full bg-clay/25 blur-3xl" />
        <div className="aurora-b absolute left-1/3 top-1/3 h-[22rem] w-[22rem] rounded-full bg-ember/10 blur-3xl" />
      </div>
      <div className="relative mx-auto grid w-full max-w-[1600px] grid-cols-1 items-center gap-10 px-6 md:gap-12 md:px-12">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: t(1), delay: d(0.9) }}
            data-testid="hero-overline"
            className="mb-5 text-xs uppercase tracking-[0.3em] text-smoke md:mb-8"
          >
            Easy Mood — Clever Goods, Est. 2026
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-serif text-[clamp(2.5rem,10.5vw,10rem)] font-medium leading-[0.92] tracking-tighter text-ink md:text-[clamp(3.5rem,11vw,10rem)]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-ember" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: t(1.1), delay: d(0.15 + i * 0.14), ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <SmileArc className="-ml-2 mt-1 w-36 sm:w-52 md:w-80" delay={d(1.15)} />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: t(0.9), delay: d(0.9), ease: EASE }}
            data-testid="hero-subcopy"
            className="mt-5 max-w-md text-base leading-relaxed text-smoke md:mt-8"
          >
            Portable gadgets and home conveniences that fold, clip, pack and
            disappear — until the exact moment you need them.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: t(0.9), delay: d(1.1), ease: EASE }}
            className="mt-6 md:mt-10"
          >
            <a
              href="#shop"
              data-testid="hero-cta-shop"
              onClick={(e) => {
                e.preventDefault();
                if (window.__lenis) {
                  window.__lenis.scrollTo("#shop", { offset: -64, duration: 1.6 });
                } else {
                  document.querySelector("#shop")?.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="group inline-flex items-center gap-3 border border-ink px-8 py-4 text-xs uppercase tracking-[0.25em] text-ink transition-colors duration-500 hover:border-ember hover:bg-ember"
            >
              Explore the Catalogue
              <ArrowDown className="h-4 w-4 transition-transform duration-500 group-hover:translate-y-1" strokeWidth={1.5} />
            </a>
          </motion.div>

        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: t(0.9), delay: d(1.2), ease: EASE }}
          className="relative"
        >
          <RotatingBadge className="absolute -top-7 right-2 z-10 h-20 w-20 sm:h-24 sm:w-24 md:-top-10 md:right-6 md:h-28 md:w-28" />
          <div className="overflow-hidden">
            <img
              src="/hero-banner.webp"
              alt="Easy Mood collection — juicer, blender, fan, speaker and more clever goods on display"
              loading="eager"
              decoding="async"
              data-testid="hero-banner"
              className="h-auto w-full scale-[1.06]"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
            transition={{
              opacity: { duration: t(0.6), delay: d(1.6) },
              scale: { duration: t(0.6), delay: d(1.6) },
              y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: d(2.2) },
            }}
            data-testid="hero-chip-price"
            className="absolute left-2 top-2 border border-line bg-bone px-3 py-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.07)] md:left-4 md:top-4 md:px-4 md:py-2"
          >
            <p className="text-[9px] uppercase tracking-[0.2em] text-smoke">From</p>
            <p className="font-serif text-lg font-medium text-ember md:text-xl">
              {minPrice !== null ? `$${minPrice}` : "—"}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
